import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { IonRow, IonCol, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonButton, IonIcon } from "@ionic/react";
import { Barn, Horse, Event, Trainer, Rider, Owner, EventEntry, Contact } from "../../../models";
import SelectHorseSection from "./SelectHorseSection";
import CogginsSection from "./CogginsSection";
import SelectOwnerSection from "./SelectOwnerSection";
import SelectRiderSection from "./SelectRiderSection";
import SelectTrainerSection from "./SelectTrainerSection";
import PrimaryContactSection from "./PrimaryContactSection";
import { PersonContext } from "../../../context/PersonContext";
import EntryCardSaveStatusChip, { SaveStatusState } from "./EntryCardSaveStatusChip";
import EntryInfoStatusChip, { EntryInfoStatus } from "./EntryInfoStatusChip";
import { CreateEntryInterface } from "../../../utilities/eventEntry/CreateEventEntryUtils";

import { addCircle, trash } from "ionicons/icons";
import { getOwnerById } from "../../../utilities/owner/Owner";
import SelectPayerSection from "./SelectPayerSection";
import EntryFormSectionHeader from "./EntryFormSectionHeader";

type CardTitle = "Horse" | "Coggins" | "Owner" | "Rider" | "Second Rider" | "Trainer" | "Primary Contact" | "Payer";
type SectionId = "horse" | "coggins" | "owner" | "rider" | "rider2" | "trainer" | "contact" | "payer";

// Define which sections have required fields
const requiredFields: Record<CardTitle, string[]> = {
    Horse: ["name"],
    Coggins: [],
    Owner: ["name", "city", "state"],
    Rider: ["name", "dob", "status", "city", "state"],
    "Second Rider": ["name", "dob", "status", "city", "state"],
    Trainer: ["name", "city", "state"],
    "Primary Contact": ["type", "name", "email", "phoneNumber", "phonenumbertype"],
    Payer: ["type", "name"]
};

interface Props {
    event: Event;
    currentEventEntry?: EventEntry;
    selectedBarn?: Barn;
    onSelect: (entryInfo: CreateEntryInterface) => void;
    onValidationChange?: (isValid: boolean) => void;
    onDirtyChange?: (isDirty: boolean) => void;
}

interface CardData {
    title: CardTitle;
    displayTitle?: string;
    content: JSX.Element;
    saveStatus?: SaveStatusState;
    validationStatus: EntryInfoStatus;
    hasRequiredFields: string[];
}

const EntryInfoSection: React.FC<Props> = ({ event, currentEventEntry, selectedBarn, onSelect, onValidationChange, onDirtyChange }) => {
    const user = useContext(PersonContext);

    // State for tracking Chip Status Values
    const [cardStatuses, setCardStatuses] = useState<Record<string, SaveStatusState>>({});
    const [cardValidationStatuses, setCardValidationStatuses] = useState<Record<CardTitle, EntryInfoStatus>>({
        Horse: "incomplete",
        Coggins: "incomplete",
        Owner: "incomplete",
        Rider: "incomplete",
        "Second Rider": "incomplete",
        Trainer: "incomplete",
        "Primary Contact": "incomplete",
        Payer: "incomplete"
    });

    // State for tracking main elements of the EntryInfoSection
    const [selectedHorse, setSelectedHorse] = useState<Horse | null | undefined>(currentEventEntry?.horse);
    const [selectedTrainer, setSelectedTrainer] = useState<Trainer | undefined>(currentEventEntry?.trainer ?? undefined);
    const [selectedRider, setSelectedRider] = useState<Rider | undefined>(currentEventEntry?.rider ?? undefined);
    const [selectedRider2, setSelectedRider2] = useState<Rider | undefined>(currentEventEntry?.secondRider ?? undefined);
    const [selectedOwner, setSelectedOwner] = useState<Owner | undefined>(currentEventEntry?.owner ?? undefined);
    const [updatedHorseFromOwner, setUpdatedHorseFromOwner] = useState<Horse | null>(null);
    const [showSecondRider, setShowSecondRider] = useState(!!currentEventEntry?.secondRider);

    const [primaryContact, setPrimaryContact] = useState<Contact | undefined>();
    const [payerName, setPayerName] = useState<string | undefined>();

    // State for tracking the Formatted Value of the EventEntry
    const [entryInfo, setEntryInfo] = useState<CreateEntryInterface>({
        userId: user.id,
        eventId: event.id || "",
        organizationId: event.organizationId || "",
        // currentBarn: selectedBarn || undefined,
        currentHorse: currentEventEntry?.horse || undefined,
        currentOwner: currentEventEntry?.owner || undefined,
        currentRider: currentEventEntry?.rider || undefined,
        currentRider2: currentEventEntry?.secondRider || undefined,
        currentTrainer: currentEventEntry?.trainer || undefined,
        currentContact: undefined,
    });

    // Track active/dirty/save across sections for outside-save behavior
    // Active = the section the user most recently clicked inside of
    // Dirty = a change in the UI that has not yet been saved in the DB
    // Save = ref to the Save functions to handle DB saves
    const [activeSection, setActiveSection] = useState<SectionId | null>(null);
    const dirtyRef = useRef<Record<string, boolean>>({});
    const saveFnsRef = useRef<Record<string, () => Promise<void> | void>>({});
    /** Ignore programmatic field hydration; only track edits after the user interacts with this section. */
    const userHasInteractedRef = useRef(false);

    // Refs to track current rider values for synchronous access in event handlers
    const selectedRiderRef = useRef<Rider | undefined>(selectedRider);
    const selectedRider2Ref = useRef<Rider | undefined>(selectedRider2);

    // Flag to track when a rider selection is in progress to prevent auto-push
    const isSelectingRiderRef = useRef<boolean>(false);

    // Track when rider 1 was last cleared to prevent immediate auto-push after clearing
    const riderClearedTimeRef = useRef<number>(0);

    // Refs to track timeout IDs for cleanup
    const autoPushTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const selectingRiderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const fieldChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleBecomeActive = useCallback((sectionId: SectionId | null) => {
        setActiveSection(sectionId);
    }, []);

    const markUserInteraction = useCallback(() => {
        userHasInteractedRef.current = true;
    }, []);

    const handleDirtyChange = useCallback((sectionId: SectionId, isDirty: boolean) => {
        if (!userHasInteractedRef.current) return;
        dirtyRef.current[sectionId] = isDirty;
        onDirtyChange?.(Object.values(dirtyRef.current).some(Boolean));
    }, [onDirtyChange]);

    const handleRegisterSave = useCallback((sectionId: string, saveFn: () => Promise<void> | void) => {
        saveFnsRef.current[sectionId] = saveFn;
    }, []);

    // Update refs whenever state changes
    useEffect(() => {
        selectedRiderRef.current = selectedRider;
    }, [selectedRider]);

    useEffect(() => {
        selectedRider2Ref.current = selectedRider2;
    }, [selectedRider2]);

    useEffect(() => {
        const onPointerDownCapture = async (e: PointerEvent | MouseEvent) => {
            const anyEvent: any = e as any;
            const targetEl = anyEvent.composedPath ? (anyEvent.composedPath()[0] as HTMLElement) : (anyEvent.target as HTMLElement);
            const clickedSection = targetEl?.closest?.("[data-section]") as HTMLElement | null;
            const clickedId = clickedSection?.dataset?.section || null;
            const currentActive = activeSection;

            // Check if click is on a select/dropdown element (which might be in a portal)
            const isSelectElement = targetEl?.closest?.("select, .select-wrapper, .react-select, [role='combobox'], [role='listbox'], .select-container, .dropdown") !== null;

            // Save any dirty changes first
            if (currentActive && dirtyRef.current[currentActive] && clickedId !== currentActive && !isSelectElement) {
                const save = saveFnsRef.current[currentActive];
                if (typeof save === "function") {
                    await save();
                }
                dirtyRef.current[currentActive] = false;
                onDirtyChange?.(Object.values(dirtyRef.current).some(Boolean));
            }

            // Special handling for rider 1 section: push rider 2 to rider 1 when clicking away
            // Only trigger when:
            // 1. Currently active on rider section
            // 2. Clicking on a DIFFERENT section (not null, not rider, not rider2)
            // 3. Not clicking on select/dropdown elements
            // 4. Not currently selecting a rider
            // Only trigger auto-push when genuinely navigating to another section
            const validSections: SectionId[] = ["horse", "coggins", "owner", "trainer", "contact", "payer"];
            const isClickingToDifferentSection = clickedId && clickedId !== "rider" && clickedId !== "rider2" && validSections.includes(clickedId as SectionId);

            if (currentActive === "rider" && isClickingToDifferentSection && !isSelectElement) {
                // Clear any existing timeout to prevent race conditions
                if (autoPushTimeoutRef.current) {
                    clearTimeout(autoPushTimeoutRef.current);
                }
                
                // Use a longer delay to ensure selection has completed
                autoPushTimeoutRef.current = setTimeout(() => {
                    const currentRider = selectedRiderRef.current;
                    const currentRider2 = selectedRider2Ref.current;
                    const isSelectingRider = isSelectingRiderRef.current;
                    const timeSinceCleared = Date.now() - riderClearedTimeRef.current;
                    const wasRecentlyCleared = riderClearedTimeRef.current > 0 && timeSinceCleared < 1000; // 1 second grace period

                    // Only push if:
                    // 1. Rider 1 is still empty
                    // 2. Rider 2 exists
                    // 3. We're not currently selecting a rider
                    // 4. The field wasn't recently cleared (giving user time to select new rider)
                    if (!currentRider && currentRider2 && !isSelectingRider && !wasRecentlyCleared) {
                        // Get fresh validation status instead of using stale closure value
                        setCardValidationStatuses((prev) => {
                            const isComplete = prev["Second Rider"] === "complete";
                            // Push rider 2 to rider 1 position
                            handleSelectedRider(currentRider2, isComplete);
                            return prev;
                        });
                        // Clear rider 2
                        handleSelectedSecondRider(undefined, false);
                        // Hide the second rider section
                        setShowSecondRider(false);
                    }
                    autoPushTimeoutRef.current = null;
                }, 200);
            }
        };
        document.addEventListener("pointerdown", onPointerDownCapture as unknown as EventListener, { capture: true });
        document.addEventListener("click", onPointerDownCapture as unknown as EventListener, { capture: true });
        return () => {
            // Cleanup timeouts when effect re-runs or component unmounts
            if (autoPushTimeoutRef.current) {
                clearTimeout(autoPushTimeoutRef.current);
                autoPushTimeoutRef.current = null;
            }
            document.removeEventListener("pointerdown", onPointerDownCapture as unknown as EventListener, { capture: true } as any);
            document.removeEventListener("click", onPointerDownCapture as unknown as EventListener, { capture: true } as any);
        };
    }, [activeSection]); // Removed cardValidationStatuses from deps to avoid stale closures

    // Sync entryInfo with currentEventEntry when it changes (e.g., when card expands or entry changes)
    useEffect(() => {
        if (currentEventEntry) {
            setEntryInfo((prevEntryInfo) => {
                const updatedEntryInfo: CreateEntryInterface = {
                    ...prevEntryInfo,
                    userId: user.id,
                    eventId: event.id || "",
                    organizationId: event.organizationId || "",
                    currentHorse: currentEventEntry.horse || prevEntryInfo.currentHorse,
                    currentOwner: currentEventEntry.owner || prevEntryInfo.currentOwner,
                    currentRider: currentEventEntry.rider || prevEntryInfo.currentRider,
                    currentRider2: currentEventEntry.secondRider || prevEntryInfo.currentRider2,
                    currentTrainer: currentEventEntry.trainer || prevEntryInfo.currentTrainer,
                };
                // Notify parent component about the updated entryInfo
                onSelect(updatedEntryInfo);
                return updatedEntryInfo;
            });
        }
    }, [currentEventEntry]);

    // Cleanup all timeouts on component unmount
    useEffect(() => {
        return () => {
            if (autoPushTimeoutRef.current) {
                clearTimeout(autoPushTimeoutRef.current);
            }
            if (selectingRiderTimeoutRef.current) {
                clearTimeout(selectingRiderTimeoutRef.current);
            }
            if (fieldChangeTimeoutRef.current) {
                clearTimeout(fieldChangeTimeoutRef.current);
            }
        };
    }, []);

    // Check validation status and notify parent when it changes
    useEffect(() => {
        if (onValidationChange) {
            const cogginsRequired = !!(event.entryOptions?.requiresCogginsData || event.entryOptions?.requiresCogginsDocument);
            const isValid = Object.entries(requiredFields).every(([title, fields]) => {
                if (fields.length === 0) return true;

                if (title === "Second Rider" && !showSecondRider) {
                    return true;
                }

                return cardValidationStatuses[title as CardTitle] === "complete";
            }) && (!cogginsRequired || cardValidationStatuses.Coggins === "complete");

            onValidationChange(isValid);
        }
    }, [cardValidationStatuses, showSecondRider, onValidationChange, event.entryOptions?.requiresCogginsData, event.entryOptions?.requiresCogginsDocument]);

    const handleCardSaveStatusChange = (cardTitle: CardTitle, status: SaveStatusState) => {
        setCardStatuses((prev) => ({ ...prev, [cardTitle]: status }));
    };

    const handleCardValidationStatusChange = (cardTitle: CardTitle, isComplete: Boolean) => {
        setCardValidationStatuses((prev) => ({ ...prev, [cardTitle]: isComplete ? "complete" : "incomplete" }));
    };

    const handleSelectedHorse = async (horse?: Horse, isComplete?: Boolean) => {
        setSelectedHorse(horse);

        // Set status to undefined when a new horse is selected
        // Set status to "Unsaved" when a new horse name is typed in but horse isn't created yet
        handleCardSaveStatusChange("Horse", !horse && isComplete ? "unsaved" : undefined);

        // Set status to "Incomplete" based on prop
        handleCardValidationStatusChange("Horse", !!isComplete);

        // Clear the selected owner first when a new horse is selected
        // This prevents the previous owner from persisting when selecting a new horse
        setSelectedOwner(undefined);

        // If horse has an owner (either full owner object or ownerId/ownerName), automatically populate the owner section
        if (horse?.owner || (horse?.ownerId && horse?.ownerName)) {
            if (horse.owner) {
                const owner = horse.owner;
                setSelectedOwner(owner);

                // Update owner card status
                handleCardSaveStatusChange("Owner", undefined);
                handleCardValidationStatusChange("Owner", true);

                // Update entry info with the owner
                const updatedEntryInfo: CreateEntryInterface = {
                    ...entryInfo,
                    currentHorse: horse,
                    currentOwner: owner,
                };
                onSelect(updatedEntryInfo);
                setEntryInfo((prevValue: CreateEntryInterface) => {
                    const updatedValue: CreateEntryInterface = {
                        ...prevValue,
                        currentHorse: horse,
                        currentOwner: owner,
                    };
                    return updatedValue;
                });
            } else if (horse.ownerId) {
                // Set the owner object here
                // Populates the SelectOwnerSection
                // Populates the Primary Contact's Owner option
                const ownerQuery = await getOwnerById(horse.ownerId);
                if (ownerQuery.isSuccess) {
                    const foundOwner: Owner = ownerQuery.result;
                    setSelectedOwner(foundOwner);

                    // Update owner card status
                    handleCardSaveStatusChange("Owner", undefined);
                    handleCardValidationStatusChange("Owner", true);

                    // Update entry info with the owner
                    const updatedEntryInfo: CreateEntryInterface = {
                        ...entryInfo,
                        currentHorse: horse,
                        currentOwner: foundOwner,
                    };
                    onSelect(updatedEntryInfo);
                    setEntryInfo((prevValue: CreateEntryInterface) => {
                        const updatedValue: CreateEntryInterface = {
                            ...prevValue,
                            currentHorse: horse,
                            currentOwner: foundOwner,
                        };
                        return updatedValue;
                    });
                }
            }
        } else {
            // If horse has no owner, clear the owner selection
            handleCardValidationStatusChange("Owner", false);
            handleCardSaveStatusChange("Owner", undefined);

            // Pass the selectedHorse value up to the parent component
            const updatedEntryInfo: CreateEntryInterface = {
                ...entryInfo,
                currentHorse: horse,
                currentOwner: undefined,
            };
            onSelect(updatedEntryInfo);
            setEntryInfo((prevValue: CreateEntryInterface) => {
                const updatedValue: CreateEntryInterface = {
                    ...prevValue,
                    currentHorse: horse,
                    currentOwner: undefined,
                };
                return updatedValue;
            });
        }
    };

    const handleSelectedRider = (rider?: Rider, isComplete?: Boolean) => {
        // Set flag to prevent auto-push during selection
        isSelectingRiderRef.current = true;

        // Track when rider is cleared to prevent immediate auto-push
        if (!rider) {
            riderClearedTimeRef.current = Date.now();
        } else {
            // Clear the timestamp when a rider is selected
            riderClearedTimeRef.current = 0;
        }

        setSelectedRider(rider);
        // Update ref immediately for synchronous access in event handlers
        selectedRiderRef.current = rider;

        handleCardSaveStatusChange("Rider", !rider && isComplete ? "unsaved" : undefined);
        handleCardValidationStatusChange("Rider", !!isComplete);

        const updatedEntryInfo: CreateEntryInterface = {
            ...entryInfo,
            currentRider: rider,
        };
        onSelect(updatedEntryInfo);
        setEntryInfo((prevValue: CreateEntryInterface) => {
            const updatedValue: CreateEntryInterface = {
                ...prevValue,
                currentRider: rider,
            };
            return updatedValue;
        });

        // Clear flag after a delay to allow click handlers and selection to complete
        // This should be longer than the auto-push check delay (200ms) to ensure protection
        // Clear any existing timeout to prevent race conditions
        if (selectingRiderTimeoutRef.current) {
            clearTimeout(selectingRiderTimeoutRef.current);
        }
        selectingRiderTimeoutRef.current = setTimeout(() => {
            isSelectingRiderRef.current = false;
            selectingRiderTimeoutRef.current = null;
        }, 300);
    };

    const handleSelectedSecondRider = (rider?: Rider, isComplete?: Boolean) => {
        setSelectedRider2(rider);
        // Update ref immediately for synchronous access in event handlers
        selectedRider2Ref.current = rider;

        handleCardSaveStatusChange("Second Rider", !rider && isComplete ? "unsaved" : undefined);
        handleCardValidationStatusChange("Second Rider", !!isComplete);

        const updatedEntryInfo: CreateEntryInterface = {
            ...entryInfo,
            currentRider2: rider,
        };
        onSelect(updatedEntryInfo);
        setEntryInfo((prevValue: CreateEntryInterface) => {
            const updatedValue: CreateEntryInterface = {
                ...prevValue,
                currentRider2: rider,
            };
            return updatedValue;
        });
    };

    const handleSelectedOwner = (owner?: Owner, isComplete?: Boolean) => {
        setSelectedOwner(owner);

        handleCardSaveStatusChange("Owner", !owner && isComplete ? "unsaved" : undefined);
        handleCardValidationStatusChange("Owner", !!isComplete);

        const updatedEntryInfo: CreateEntryInterface = {
            ...entryInfo,
            currentOwner: owner,
        };
        onSelect(updatedEntryInfo);
        setEntryInfo((prevValue: CreateEntryInterface) => {
            const updatedValue: CreateEntryInterface = {
                ...prevValue,
                currentOwner: owner,
            };
            return updatedValue;
        });
    };

    const handleSelectedTrainer = (trainer?: Trainer, isComplete?: Boolean) => {
        setSelectedTrainer(trainer);

        handleCardSaveStatusChange("Trainer", !trainer && isComplete ? "unsaved" : undefined);
        handleCardValidationStatusChange("Trainer", !!isComplete);

        const updatedEntryInfo: CreateEntryInterface = {
            ...entryInfo,
            currentTrainer: trainer,
        };
        onSelect(updatedEntryInfo);
        setEntryInfo((prevValue: CreateEntryInterface) => {
            const updatedValue: CreateEntryInterface = {
                ...prevValue,
                currentTrainer: trainer,
            };
            return updatedValue;
        });
    };

    const handleSelectedContact = (contact?: Contact, isComplete?: Boolean) => {
        setPrimaryContact(contact);

        handleCardSaveStatusChange("Primary Contact", !contact && isComplete ? "unsaved" : undefined);
        handleCardValidationStatusChange("Primary Contact", !!isComplete);

        const updatedEntryInfo: CreateEntryInterface = {
            ...entryInfo,
            currentContact: contact,
        };
        onSelect(updatedEntryInfo);
        setEntryInfo((prevValue: CreateEntryInterface) => {
            const updatedValue: CreateEntryInterface = {
                ...prevValue,
                currentContact: contact,
            };
            return updatedValue;
        });
    };

    const handleSelectedPayer = (payerName?: string, isComplete?: Boolean) => {
        setPayerName(payerName);

        handleCardValidationStatusChange("Payer", !!isComplete);

        const updatedEntryInfo: CreateEntryInterface = {
            ...entryInfo,
            payerName: payerName
        };
        onSelect(updatedEntryInfo);
        setEntryInfo((prevValue: CreateEntryInterface) => {
            const updatedValue: CreateEntryInterface = {
                ...prevValue,
                payerName: payerName
            };
            return updatedValue;
        });
    };

    const handleRemoveSecondRiderCard = () => {
        setShowSecondRider(false);
        handleSelectedSecondRider(undefined, false);
    };

    const cardsData: CardData[] = [
        {
            title: "Horse" as CardTitle,
            content: (
                <SelectHorseSection
                    onRegisterSave={(fn: () => Promise<void> | void) => handleRegisterSave("horse", fn)}
                    requiredFields={requiredFields["Horse"]}
                    horse={currentEventEntry?.horse}
                    barn={selectedBarn}
                    updatedHorse={updatedHorseFromOwner}
                    onSelectHorse={(horse?: Horse, isComplete?: Boolean) => {
                        handleSelectedHorse(horse, isComplete);
                        handleBecomeActive("horse");
                    }}
                    onSavingStateChange={(isSaving: boolean) => {
                        if (isSaving) {
                            handleCardSaveStatusChange("Horse", "saving");
                        } else {
                            handleCardSaveStatusChange("Horse", "saved");
                        }
                    }}
                    onFieldChange={(horse?: Horse, isComplete?: Boolean) => {
                        if (horse) {
                            handleCardValidationStatusChange("Horse", !!isComplete);
                        } else {
                            handleCardValidationStatusChange("Horse", false);
                        }
                        handleCardSaveStatusChange("Horse", "unsaved");
                        handleDirtyChange("horse", true);
                        handleBecomeActive("horse");
                    }}
                />
            ),
            saveStatus: cardStatuses.Horse,
            validationStatus: cardValidationStatuses.Horse,
            hasRequiredFields: requiredFields.Horse,
        },
        {
            title: "Coggins" as CardTitle,
            content: (
                <CogginsSection
                    onRegisterSave={(fn: () => Promise<void> | void) => handleRegisterSave("coggins", fn)}
                    event={event}
                    user={user}
                    selectedHorse={selectedHorse || currentEventEntry?.horse}
                    onSavingStateChange={(isSaving: boolean) => {
                        if (isSaving) {
                            handleCardSaveStatusChange("Coggins", "saving");
                        } else {
                            handleCardSaveStatusChange("Coggins", "saved");
                        }
                    }}
                    onFieldChange={(isComplete?: Boolean) => {
                        handleCardValidationStatusChange("Coggins", !!isComplete);
                        if (selectedHorse) handleCardSaveStatusChange("Coggins", "unsaved");
                        else handleCardSaveStatusChange("Coggins", undefined);
                        handleBecomeActive("coggins");
                        handleDirtyChange("coggins", true);
                    }}
                />
            ),
            saveStatus: cardStatuses.Coggins,
            validationStatus: cardValidationStatuses.Coggins,
            hasRequiredFields: requiredFields.Coggins,
        },
        {
            title: "Owner",
            content: (
                <SelectOwnerSection
                    onRegisterSave={(fn: () => Promise<void> | void) => handleRegisterSave("owner", fn)}
                    requiredFields={requiredFields["Owner"]}
                    selectedOwner={currentEventEntry?.owner}
                    barn={selectedBarn}
                    horse={selectedHorse}
                    onSelectOwner={(owner?: Owner, isComplete?: Boolean) => {
                        handleSelectedOwner(owner, isComplete);
                        handleBecomeActive("owner");
                    }}
                    onSavingStateChange={(isSaving: boolean) => {
                        if (isSaving) {
                            handleCardSaveStatusChange("Owner", "saving");
                        } else {
                            handleCardSaveStatusChange("Owner", "saved");
                        }
                    }}
                    onFieldChange={(owner?: Owner, isComplete?: Boolean) => {
                        if (owner) {
                            handleCardValidationStatusChange("Owner", !!isComplete);
                        } else {
                            handleCardValidationStatusChange("Owner", false);
                        }
                        handleCardSaveStatusChange("Owner", "unsaved");
                        handleDirtyChange("owner", true);
                        handleBecomeActive("owner");
                    }}
                    onHorseUpdated={(updatedHorse: Horse) => {
                        // Pass the updated horse directly to SelectHorseSection
                        setUpdatedHorseFromOwner(updatedHorse);
                        // Also update the selected horse state
                        setSelectedHorse(updatedHorse);
                    }}
                />
            ),
            saveStatus: cardStatuses["Owner"],
            validationStatus: cardValidationStatuses.Owner,
            hasRequiredFields: requiredFields.Owner,
        },
        {
            title: "Rider",
            displayTitle: "Rider 1",
            content: (
                <SelectRiderSection
                    onRegisterSave={(fn: () => Promise<void> | void) => handleRegisterSave("rider", fn)}
                    requiredFields={requiredFields["Rider"]}
                    selectedRider={selectedRider}
                    barn={selectedBarn}
                    excludedRiderId={selectedRider2?.id}
                    disabledRiderLabel="Selected as Rider 2"
                    sectionId="rider"
                    onSelectRider={(rider?: Rider, isComplete?: Boolean) => {
                        // Set flag immediately to prevent auto-push during selection
                        // This runs before handleSelectedRider, so it helps with the deferred check
                        isSelectingRiderRef.current = true;
                        handleSelectedRider(rider, isComplete);
                        handleBecomeActive("rider");
                    }}
                    onSavingStateChange={(isSaving: boolean) => {
                        if (isSaving) {
                            handleCardSaveStatusChange("Rider", "saving");
                        } else {
                            handleCardSaveStatusChange("Rider", "saved");
                        }
                    }}
                    onFieldChange={(rider?: Rider, isComplete?: Boolean) => {
                        // Set flag when user is editing rider field to prevent auto-push
                        isSelectingRiderRef.current = true;
                        // Clear any existing timeout to prevent race conditions
                        if (fieldChangeTimeoutRef.current) {
                            clearTimeout(fieldChangeTimeoutRef.current);
                        }
                        // Clear flag after delay
                        fieldChangeTimeoutRef.current = setTimeout(() => {
                            isSelectingRiderRef.current = false;
                            fieldChangeTimeoutRef.current = null;
                        }, 300);

                        if (rider) {
                            handleCardValidationStatusChange("Rider", !!isComplete);
                        } else {
                            handleCardValidationStatusChange("Rider", false);
                        }
                        handleCardSaveStatusChange("Rider", "unsaved");
                        handleDirtyChange("rider", true);
                        handleBecomeActive("rider");
                    }}
                />
            ),
            saveStatus: cardStatuses["Rider"],
            validationStatus: cardValidationStatuses.Rider,
            hasRequiredFields: requiredFields.Rider,
        },
        ...(showSecondRider
            ? [
                  {
                      title: "Second Rider" as CardTitle,
                      displayTitle: "Rider 2",
                      content: (
                          <SelectRiderSection
                              onRegisterSave={(fn: () => Promise<void> | void) => handleRegisterSave("rider2", fn)}
                              requiredFields={requiredFields["Second Rider"]}
                              selectedRider={selectedRider2}
                              barn={selectedBarn}
                              excludedRiderId={selectedRider?.id}
                              disabledRiderLabel="Selected as Rider 1"
                              sectionId="rider2"
                              onSelectRider={(secondRider?: Rider, isComplete?: Boolean) => {
                                  handleSelectedSecondRider(secondRider, isComplete);
                                  handleBecomeActive("rider2");
                              }}
                              onSavingStateChange={(isSaving: boolean) => {
                                    if (isSaving) {
                                        handleCardSaveStatusChange("Second Rider", "saving");
                                    } else {
                                        handleCardSaveStatusChange("Second Rider", "saved");
                                    }
                              }}
                              onFieldChange={(rider?: Rider, isComplete?: Boolean) => {
                                  if (rider) {
                                      handleCardValidationStatusChange("Second Rider", !!isComplete);
                                  } else {
                                      handleCardValidationStatusChange("Second Rider", false);
                                  }
                                  handleCardSaveStatusChange("Second Rider", "unsaved");
                                  handleDirtyChange("rider2", true);
                                  handleBecomeActive("rider2");
                              }}
                          />
                      ),
                      saveStatus: cardStatuses["Second Rider"],
                      validationStatus: cardValidationStatuses["Second Rider"],
                      hasRequiredFields: requiredFields["Second Rider"],
                  },
              ]
            : []),
        {
            title: "Trainer",
            content: (
                <SelectTrainerSection
                    onRegisterSave={(fn: () => Promise<void> | void) => handleRegisterSave("trainer", fn)}
                    requiredFields={requiredFields["Trainer"]}
                    selectedTrainer={currentEventEntry?.trainer}
                    barn={selectedBarn}
                    onSelectTrainer={(trainer?: Trainer, isComplete?: Boolean) => {
                        handleSelectedTrainer(trainer, isComplete);
                        handleBecomeActive("trainer");
                    }}
                    onSavingStateChange={(isSaving: boolean) => {
                        if (isSaving) {
                            handleCardSaveStatusChange("Trainer", "saving");
                        } else {
                            handleCardSaveStatusChange("Trainer", "saved");
                        }
                    }}
                    onFieldChange={(trainer?: Trainer, isComplete?: Boolean) => {
                        if (trainer) {
                            handleCardValidationStatusChange("Trainer", !!isComplete);
                        } else {
                            handleCardValidationStatusChange("Trainer", false);
                        }
                        handleCardSaveStatusChange("Trainer", "unsaved");
                        handleDirtyChange("trainer", true);
                        handleBecomeActive("trainer");
                    }}
                />
            ),
            saveStatus: cardStatuses["Trainer"],
            validationStatus: cardValidationStatuses.Trainer,
            hasRequiredFields: requiredFields.Trainer,
        },
        {
            title: "Primary Contact",
            content: (
                <PrimaryContactSection
                    onRegisterSave={(fn: () => Promise<void> | void) => handleRegisterSave("contact", fn)}
                    requiredFields={requiredFields["Primary Contact"]}
                    selectedContactId={currentEventEntry?.contactId}
                    rider={selectedRider}
                    secondRider={selectedRider2}
                    owner={selectedOwner}
                    trainer={selectedTrainer}
                    onSelectContact={(contact?: Contact, isComplete?: Boolean) => {
                        handleSelectedContact(contact, isComplete);
                        handleBecomeActive("contact");
                    }}
                    onSavingStateChange={(isSaving: boolean) => {
                        if (isSaving) {
                            handleCardSaveStatusChange("Primary Contact", "saving");
                        } else {
                            handleCardSaveStatusChange("Primary Contact", "saved");
                        }
                    }}
                    onFieldChange={(contact?: Contact, isComplete?: Boolean) => {
                        if (contact) {
                            handleCardValidationStatusChange("Primary Contact", !!isComplete);
                        } else {
                            handleCardValidationStatusChange("Primary Contact", false);
                        }
                        handleCardSaveStatusChange("Primary Contact", "unsaved");
                        handleDirtyChange("contact", true);
                        handleBecomeActive("contact");
                    }}
                />
            ),
            saveStatus: cardStatuses["Primary Contact"],
            validationStatus: cardValidationStatuses["Primary Contact"],
            hasRequiredFields: requiredFields["Primary Contact"],
        },
        {
            title: "Payer",
            content: (
                <SelectPayerSection
                    requiredFields={requiredFields["Payer"]}
                    selectedPayerName={currentEventEntry?.payerName}
                    rider={selectedRider}
                    secondRider={selectedRider2}
                    owner={selectedOwner}
                    trainer={selectedTrainer}
                    onSelectPayerPerson={(payerName?: string, isComplete?: Boolean) => {
                        handleSelectedPayer(payerName, isComplete);
                        handleBecomeActive("payer");
                    }}
                    onFieldChange={(payerName?: string, isComplete?: Boolean) => {
                        if (payerName) {
                            handleCardValidationStatusChange("Payer", !!isComplete);
                        } else {
                            handleCardValidationStatusChange("Payer", false);
                        }
                        handleDirtyChange("payer", true);
                        handleBecomeActive("payer");
                    }}
                />
            ),
            saveStatus: cardStatuses["Payer"],
            validationStatus: cardValidationStatuses["Payer"],
            hasRequiredFields: requiredFields["Payer"],
        },
    ];

    return (
        <div onPointerDownCapture={markUserInteraction} onFocusCapture={markUserInteraction}>
            <EntryFormSectionHeader title="Add Entry Info" emoji="🐴" />

            {/* Entry Info Sections */}
            {cardsData.map((card) => {
                const showAddAnotherRider =
                    card.title === "Rider" &&
                    !!event.entryOptions?.displayRider2Input &&
                    cardValidationStatuses.Rider === "complete" &&
                    !showSecondRider;

                return (
                <React.Fragment key={card.title}>
                    <IonCard
                        className="rounded-3 p-0 mx-0 bg-white entry-form-content-gap-bottom entry-info-subcard"
                        style={{ overflow: "visible", marginTop: 0 }}>
                        {/* Card Header */}
                        <IonCardHeader className="px-2 py-0">
                            <IonRow className="ion-align-items-center">
                                <IonCol sizeXs="12" className="px-0 pb-0 d-flex justify-content-between align-items-center">
                                    <IonCardTitle className="font-weight-bold text-primary m-1">
                                        {card.displayTitle || card.title}
                                    </IonCardTitle>
                                    <div className="d-flex align-items-center">
                                        {card.saveStatus !== undefined && card.saveStatus !== "unsaved" && <EntryCardSaveStatusChip stateValue={card.saveStatus} />}
                                        {card.validationStatus && <EntryInfoStatusChip status={card.validationStatus} />}
                                        {card.title === "Second Rider" && (
                                            <IonIcon
                                                className="card-icon-size-2 text-danger ml-2 cursor-pointer"
                                                icon={trash}
                                                onClick={() => {
                                                    handleRemoveSecondRiderCard();
                                                }}
                                            />
                                        )}
                                    </div>
                                </IonCol>
                            </IonRow>
                        </IonCardHeader>

                        {/* Card Content */}
                        <IonCardContent>{card.content}</IonCardContent>
                    </IonCard>

                    {/* After Rider 1: match Add Another Entry CTA (success), not membership tertiary button */}
                    {showAddAnotherRider && (
                            <IonRow className="ion-justify-content-center entry-form-content-gap-bottom">
                                <IonCol sizeXs="12" sizeMd="10" className="ion-text-center">
                                    <IonButton
                                        expand="block"
                                        color="success"
                                        className="font-weight-bold m-0 shadow-lg--hover ion-button-wrap"
                                        onClick={() => setShowSecondRider(true)}>
                                        <IonIcon icon={addCircle} slot="start" />
                                        Add Another Rider
                                    </IonButton>
                                </IonCol>
                            </IonRow>
                        )}
                </React.Fragment>
                );
            })}
        </div>
    );
};

export default EntryInfoSection;
